import { Option } from "fp";

import React from "react";
import styled, { css } from "styled-components";

import * as Animation from "~/Animation";
import * as Color from "~/Color";
import * as Layout from "~/Layout";
import { Order } from "~/Order";
import * as Popover from "~/Popover";
import * as Shadow from "~/Shadow";
import { Time } from "~/Time";
import { Timeline } from "~/Timeline";
import * as Typography from "~/Typography";

export function Event<Event extends Event.Any>({
  Description,
  href,
  ...props
}: Event.Props<Event>) {
  return (
    <Event.Container
      {...props}
      onClick={() =>
        href &&
        // eslint-disable-next-line functional/immutable-data
        (window.location.href = `${window.location.pathname}#${href(
          props.event
        )}`)
      }
    >
      <Event.Hover
        Title={() => <Event.Timing {...props.event} />}
        Content={() => <Description {...props.event} />}
      />
      <Event.Description>
        <Description {...props.event} />
      </Event.Description>
      <Event.Line background={props.background(props.event)} />
      <Event.Description>
        <Event.Timing {...props.event} />
      </Event.Description>
    </Event.Container>
  );
}

export namespace Event {
  export type Any = Time.Interval;

  type Location = { readonly left: Percentage; readonly right: Percentage };
  type Percentage = number;

  export type Props<Event extends Any> = Container<Event> & Config<Event>;
  export type Config<Event extends Any> = {
    readonly href?: (event: Event) => string;
    readonly background: (event: Event) => Color.Color;
    readonly Description: React.Fn<Event>;
    readonly compareFn?: Order.Compare<Event>;
  };

  export const zIndex = () => 2 as const;

  const translateHover = () => "-0.2em" as const;
  const translateActive = () => "-0.1em" as const;

  export const location = (timeline: Timeline, event: Any): Location =>
    pipe(
      Time.Interval.when(event, {
        stopped: (stopped) => stopped,
        started: ({ interval: { start } }) => ({
          interval: {
            start,
            stop: timeline.now ?? timeline.interval.stop,
          },
        }),
      }),
      ({ interval: { start, stop } }) =>
        pipe(Time.Interval.Stopped.toDuration(timeline), ({ duration }) => ({
          interval: {
            start:
              (start.dateTime - timeline.interval.start.dateTime) / duration,
            stop: (timeline.interval.stop.dateTime - stop.dateTime) / duration,
          },
        })),
      ({ interval: { start, stop } }) => ({
        left: start < 0 ? 0 : start > 1 ? 1 : start,
        right: stop < 0 ? 0 : stop > 1 ? 1 : stop,
      })
    );

  export const clamp = <Event extends Any>(timeline: Timeline) => (
    event: Event
  ): Option<Event> =>
    pipe(
      Option.fromNullable(timeline.now),
      Option.filterMap((now) =>
        event.interval.start.dateTime > now.dateTime
          ? Option.none
          : Option.some({
              ...event,
              ...Time.Interval.when(event, {
                started: Time.Interval.Started.toStopped(now),
                stopped: ({ interval: { start, stop } }) => ({
                  interval: {
                    start,
                    stop: { dateTime: Math.min(now.dateTime, stop.dateTime) },
                  },
                }),
              }),
            })
      )
    );

  export const fitsOnSameLine = <Event extends Any>(later: Event) => (
    earlier: Event
  ): boolean =>
    Time.Interval.when(earlier, {
      started: () => false,
      stopped: ({ interval: { stop } }) =>
        stop.dateTime <= later.interval.start.dateTime,
    });

  type Container<Event extends Any> = {
    readonly timeline: Timeline;
    readonly event: Event;
    readonly children?: Component.Children;
  };

  export const Container = styled.div<Container<Any>>`
    z-index: ${() => zIndex()};
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    ${({ timeline, event }) =>
      pipe(
        location(timeline, event),
        ({ left, right }) =>
          css`
            left: ${left * 100}%;
            right: ${right * 100}%;
          `
      )};

    height: ${() => Timeline.Row.height()};
    cursor: pointer;

    ${() => Animation.Transition.styles(Animation.medium())};

    &:hover {
      z-index: ${() => zIndex() + 1};
      transform: translateY(${() => translateHover()});
      ${() => Animation.Transition.styles(Animation.fast())}

      & > div:nth-child(2) > div {
        ${() => Line.Hover.styles()}
      }
    }

    &:active {
      transform: translateY(${() => translateActive()});

      & > div:nth-child(2) > div {
        ${() => Line.Active.styles()}
      }
    }
  `;

  // TODO: Make available anywhere, not just when description is in view.
  export type Hover = {
    readonly Title: Component;
    readonly Content: Component;
  };

  export function Hover({ Title, Content }: Hover) {
    return (
      <Popover.Component
        placement="topLeft"
        Title={() => (
          <Hover.Container>
            <Title />
          </Hover.Container>
        )}
        Content={() => (
          <Hover.Container>
            <Content />
          </Hover.Container>
        )}
      >
        <Hover.Trigger />
      </Popover.Component>
    );
  }

  export namespace Hover {
    export const Container = styled.div`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    `;

    export const Trigger = styled.div`
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      max-width: 35em;
      width: 100%;
    `;
  }

  export function Description({
    children,
  }: {
    readonly children?: Component.Children;
  }) {
    return (
      <Description.Container>
        <Description.ForceWrapping />
        {children}
      </Description.Container>
    );
  }

  export namespace Description {
    export const Container = styled.div`
      box-sizing: border-box;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      overflow: hidden;
      height: ${() =>
        `calc((${Timeline.Row.height()} - ${Line.height()}) / 2)`};

      &:first-of-type > div {
        padding-top: 0.4em;
      }

      &:last-of-type > div {
        padding-bottom: 0.4em;
      }
    `;

    /**
     * Since we're using flex box, a "dummy" `Item` forces the first real piece of
     * content to wrap and be hidden instead of visually cutting it off.
     */
    export const ForceWrapping = () => <Description.Item />;
    export const Item = styled.div`
      box-sizing: border-box;
      display: flex;
      min-width: 0;
      flex-grow: 0;
      flex-shrink: 0;
      align-items: center;
      height: 100%;

      white-space: nowrap;
      font-size: ${() => Typography.FontSize.data()};
    `;
  }

  export type Line = { readonly background: Color.Color };
  export function Line(line: Line) {
    return (
      <Line.Container {...line}>
        <Line.Above />
        <Line.Background {...line} />
        <Line.Below />
      </Line.Container>
    );
  }

  export namespace Line {
    export const height = () => "0.4em" as const;

    export const Above = styled.div`
      z-index: ${() => zIndex() + 1};
      opacity: 0;
      background: ${() => Color.white()};
    `;

    export const Background = styled.div<Line>`
      z-index: ${() => zIndex()};
      background: ${({ background }) => background};
    `;

    export const Below = styled.div`
      z-index: ${() => zIndex() - 1};
      ${() => Shadow.Box.styles(Shadow.medium())};
    `;

    export const Container = styled.div<Line>`
      position: relative;
      height: ${() => height()};

      & > div {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        ${() => Animation.Transition.styles(Animation.medium())}
      }
    `;

    export namespace Hover {
      export const styles = () => css`
        & {
          ${() => Animation.Transition.styles(Animation.fast())}
        }

        &:first-of-type {
          opacity: 0.2;
        }

        &:last-of-type {
          ${() => Shadow.Box.styles(Shadow.small())}
        }
      `;
    }

    export namespace Active {
      export const styles = () => css`
        &:first-of-type {
          opacity: 0.1;
        }

        &:last-of-type {
          ${() => Shadow.Box.styles(Shadow.small())}
        }
      `;
    }
  }

  // TODO: Add visual `stop` time to intervals
  export function Timing({ interval }: Any) {
    const StoppedDuration = () =>
      Time.Interval.when(
        { interval },
        {
          started: () => null,
          stopped: flow(Time.Interval.Stopped.toDuration, (duration) => (
            <Time.Duration {...duration} />
          )),
        }
      );

    return (
      <>
        <Description.Item>
          <Layout.Inline.Component right>
            <Typography.Component strong>
              <Time.DateTime {...interval.start} />
            </Typography.Component>
          </Layout.Inline.Component>
        </Description.Item>
        <Description.Item>
          <StoppedDuration />
          &nbsp;
        </Description.Item>
      </>
    );
  }
}
